import { ElementType, FunctionComponent, memo, ReactNode, useMemo } from 'react'

import isEqualReact from 'fast-deep-equal/es6/react'

import config from '@config'
import { Crops } from '@utils/cook/types'
import useAppTheme from '@hooks/useAppTheme'

import Existing from './Existing'
import Missing from './Missing'
import DefaultPlaceholder, { PlaceholderProps } from './Placeholder'
import { ImageConstraints, OriginalImageDimensions } from './types'
import { calculateActualDimensions } from './utils'

export interface FreeRatioImageProps {
  src: string
  alt: string
  originalDimensions: OriginalImageDimensions
  constraints: ImageConstraints
  crops: Crops<'free'>
  Placeholder?: ElementType<PlaceholderProps>
  loading?: HTMLImageElement['loading']
  isLazy?: boolean
  className?: string
  children?: ReactNode
  darkModeImage?: Pick<
    FreeRatioImageProps,
    'src' | 'crops' | 'originalDimensions'
  >
}

const {
  image: { missingImageSrc },
} = config

const FreeRatioImage: FunctionComponent<FreeRatioImageProps> = (props) => {
  const {
    src: initialSrc,
    originalDimensions: initialOriginalDimensions,
    constraints,
    crops: initialCrops,
    loading,
    isLazy = true,
    Placeholder = DefaultPlaceholder,
    children,
    className,
    darkModeImage,
    ...restOfProps
  } = props

  const appTheme = useAppTheme()

  const { src, crops, originalDimensions } =
    appTheme === 'dark' && darkModeImage?.src
      ? {
          src: darkModeImage.src,
          crops: darkModeImage.crops,
          originalDimensions: darkModeImage.originalDimensions,
        }
      : {
          src: initialSrc,
          crops: initialCrops,
          originalDimensions: initialOriginalDimensions,
        }

  const freeCrop = crops['free']
  const croppedOriginalDimensions = freeCrop
    ? { width: freeCrop.width, height: freeCrop.height }
    : originalDimensions

  const actualDimensions = useMemo(
    () =>
      calculateActualDimensions({
        constraints,
        originalDimensions: {
          width: croppedOriginalDimensions.width,
          height: croppedOriginalDimensions.height,
        },
      }),
    [
      constraints,
      croppedOriginalDimensions.height,
      croppedOriginalDimensions.width,
    ],
  )

  if (src === missingImageSrc) {
    return (
      <Missing
        actualDimensions={actualDimensions}
        className={className}
        Placeholder={Placeholder}
      >
        {children}
      </Missing>
    )
  }

  const loadingAttr = loading ?? (isLazy ? 'lazy' : undefined)

  return (
    <Existing
      src={src}
      crops={crops}
      actualDimensions={actualDimensions}
      className={className}
      loading={loadingAttr}
      Placeholder={Placeholder}
      {...restOfProps}
    >
      {children}
    </Existing>
  )
}

const MemoizedFreeRatioImage = memo(FreeRatioImage, (prevProps, nextProps) =>
  isEqualReact(prevProps, nextProps),
)

MemoizedFreeRatioImage.displayName = 'MemoizedFreeRatioImage'

export default MemoizedFreeRatioImage
